import { type EventFilterCriteria } from "@/schemas/events/filter/EventFilterCriteria";
import { type UserFilterCriteria } from "@/schemas/profile/filters/UserFilterCriteria";
import { type FilterByRange } from "@/schemas/screen/filters/FilterByRange";
import {
  type FilterCriteria,
  FilterType,
} from "@/schemas/screen/filters/FilterCriteria";

function isRangeActive<T>(range: FilterByRange<T> | null): boolean {
  return (
    range !== null && (range.minValueId !== null || range.maxValueId !== null)
  );
}

function isUserFilterActivated(filters: UserFilterCriteria): boolean {
  return (
    isRangeActive(filters.age) ||
    isRangeActive(filters.height) ||
    isRangeActive(filters.weight) ||
    (filters.exclude !== null && filters.exclude.length > 0)
  );
}

function isEventFilterActivated(filters: EventFilterCriteria): boolean {
  return (
    (filters.category &&
      filters.category.values &&
      filters.category.values.length > 0) ||
    (filters.status &&
      filters.status.values &&
      filters.status.values.length > 0) ||
    (filters.location &&
      filters.location.values &&
      filters.location.values.length > 0) ||
    false
  );
}

export function isFiltersActivated(filters: FilterCriteria | null): boolean {
  if (filters === null) {
    return false;
  }

  switch (filters.type) {
    case FilterType.USER:
      return isUserFilterActivated(filters);
    case FilterType.EVENT:
      return isEventFilterActivated(filters);
  }
}

function countUserActiveFilters(filters: UserFilterCriteria): number {
  let count = 0;

  // Age filter
  if (isRangeActive(filters.age)) {
    count++;
  }

  // Height filter
  if (isRangeActive(filters.height)) {
    count++;
  }

  // Weight filter
  if (isRangeActive(filters.weight)) {
    count++;
  }

  // Excluded users filter
  if (filters.exclude !== null && filters.exclude.length > 0) {
    count++;
  }

  // Avatar filter
  if (filters.hasAvatar !== null) {
    count++;
  }

  return count;
}

function countEventActiveFilters(filters: EventFilterCriteria): number {
  let count = 0;

  // Category filter
  if (filters.category?.values && filters.category.values.length > 0) {
    count++;
  }

  // Status filter
  if (filters.status?.values && filters.status.values.length > 0) {
    count++;
  }

  // Location filter
  if (filters.location?.values && filters.location.values.length > 0) {
    count++;
  }

  return count;
}

/**
 * Counts the number of active filters in the provided filter criteria
 * @param filters - The filter criteria to check
 * @returns The number of active filters, or 0 if no filters are provided
 */
export function countActiveFilters(filters: FilterCriteria | null): number {
  if (filters === null) {
    return 0;
  }

  switch (filters.type) {
    case FilterType.USER:
      return countUserActiveFilters(filters);
    case FilterType.EVENT:
      return countEventActiveFilters(filters);
    default:
      return 0;
  }
}
